import React from 'react';
import { Flex, Text, Card, Image, Icon, ListItem } from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';

import { InternalLink } from 'platform/react-router';

import { getHomeConditionRoute } from 'shared/helpers/routes';
import { useObservability } from 'shared/helpers/observability';

import { AutoScrollCarousel } from 'sellDirect/components/AutoScrollCarousel';

interface EntryPointProps {
  showMarketConditionCard?: boolean;
  showPriceAdjustmentCard: boolean;
  mlsImageUrl?: string;
}

/**
 * Entry point for the price adjustment experiment
 * component-test-check-ignore
 */
export const EntryPoint: React.FC<EntryPointProps> = ({
  showMarketConditionCard,
  showPriceAdjustmentCard,
  mlsImageUrl,
}) => {
  return (
    <Flex as="ul" flexDir={['column', null, 'row']} gridGap={4}>
      {showPriceAdjustmentCard && (
        <ListItem flex="1">
          <UpdatedHomeCard mlsImageUrl={mlsImageUrl} />
        </ListItem>
      )}
      {showMarketConditionCard && (
        <ListItem flex="1">
          <MarketCard />
        </ListItem>
      )}
    </Flex>
  );
};

interface CardWithImageProps {
  title: string;
  subtitle: string;
  imageUrl?: string;
  withChevron?: boolean;
}

/** Cards to use for the Price Adjustment entry point */
const CardWithImage: React.FC<CardWithImageProps> = ({
  title,
  subtitle,
  imageUrl,
  withChevron,
}) => {
  return (
    <Card
      border="1px solid black"
      borderRadius="semiRounded"
      borderColor="neutrals30"
      p="5"
      height="100%"
    >
      <Flex alignItems="center" justifyContent="space-between" width="100%">
        <Flex gridGap={['5', null, '3']}>
          {imageUrl && (
            <Image
              src={imageUrl}
              alt=""
              aria-hidden
              height={['60px', null, '40px']}
              width={['60px', null, '40px']}
              objectFit="cover"
              borderRadius="roundedSquare"
              alignSelf="center"
            />
          )}
          <Flex flexDir="column" justifyContent="center">
            <Text fontWeight="semibold" size="s00">
              {title}
            </Text>
            <Text size="s00">{subtitle}</Text>
          </Flex>
        </Flex>
        {withChevron && (
          <Icon size={16} name="chevron-right" color="neutrals100" aria-hidden="true" />
        )}
      </Flex>
    </Card>
  );
};

/**
 * Carousel that serves as the Entry Point for the price adjustment experience
 * component-test-check-ignore
 */
export const EntryPointCarousel: React.FC<EntryPointProps> = ({
  showMarketConditionCard,
  showPriceAdjustmentCard,
  mlsImageUrl,
}) => {
  const { isMobile } = useDevice();
  return isMobile && showMarketConditionCard && showPriceAdjustmentCard ? (
    <AutoScrollCarousel ms={5000} width={100} snapAdjustment={150} transition="ease 1s">
      <UpdatedHomeCard mlsImageUrl={mlsImageUrl} />
      <MarketCard />
    </AutoScrollCarousel>
  ) : (
    <EntryPoint
      showMarketConditionCard={showMarketConditionCard}
      showPriceAdjustmentCard={showPriceAdjustmentCard}
      mlsImageUrl={mlsImageUrl}
    />
  );
};

const MarketCard = () => (
  <CardWithImage
    title="Market uncertainty is impacting this offer."
    subtitle="We’ll refresh it every few weeks so you have our most up-to-date offer."
  />
);

const UpdatedHomeCard: React.FC<{ mlsImageUrl: string | undefined }> = ({ mlsImageUrl }) => {
  const { trackEvent } = useObservability();

  return (
    <InternalLink
      analyticsName="cake-price-adjustment-entry-point"
      aria-label=""
      to={getHomeConditionRoute()}
      onClick={() => trackEvent('home-conditions-flow', 'entry-point')}
    >
      <CardWithImage
        title="Have you made changes to your home?"
        subtitle="See how they could affect your final offer."
        imageUrl={mlsImageUrl}
        withChevron
      />
    </InternalLink>
  );
};
